/**
 * EntryView — Three-way entry screen: Guest, Login, Sign up.
 * Shown to first-time visitors who are not authenticated.
 */

import type { AweniContext } from '../../../context/AweniContext';
import { createAuthLayout } from '../components/AuthLayout';

export async function mountEntryView(
  container: HTMLElement,
  ctx: AweniContext,
  opts: { onNavigate: (view: string, params?: Record<string, string>) => void },
): Promise<{ unmount: () => void }> {
  const t = ctx.i18n.t.bind(ctx.i18n);
  const layout = createAuthLayout(ctx);
  layout.setTitle(t('auth_entry_title'));
  layout.setSubtitle(t('auth_entry_subtitle'));

  _injectEntryStyles();

  const choices = [
    {
      view: 'guest',
      title: t('auth_entry_guest_button'),
      sub: t('auth_entry_guest_subtitle'),
      icon: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="M12 5l7 7-7 7"/></svg>`,
      kind: 'primary' as const,
    },
    {
      view: 'login',
      title: t('auth_entry_login_button'),
      sub: t('auth_entry_login_subtitle'),
      icon: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>`,
      kind: 'secondary' as const,
    },
    {
      view: 'signup',
      title: t('auth_entry_signup_button'),
      sub: t('auth_entry_signup_subtitle'),
      icon: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>`,
      kind: 'secondary' as const,
    },
  ];

  const list = document.createElement('div');
  list.className = 'auth-entry-list';

  for (const c of choices) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `auth-entry-btn auth-entry-btn-${c.kind}`;
    btn.innerHTML = `
      <span class="auth-entry-icon">${c.icon}</span>
      <span class="auth-entry-text">
        <span class="auth-entry-title">${c.title}</span>
        <span class="auth-entry-sub">${c.sub}</span>
      </span>
    `;
    btn.addEventListener('click', () => opts.onNavigate(c.view));
    list.appendChild(btn);
  }

  layout.card.appendChild(list);
  container.innerHTML = '';
  container.appendChild(layout.wrapper);

  return {
    unmount() {
      container.innerHTML = '';
    },
  };
}

function _injectEntryStyles(): void {
  if (document.getElementById('aweni-entry-styles')) return;
  const style = document.createElement('style');
  style.id = 'aweni-entry-styles';
  style.textContent = `
    .auth-entry-list { display: flex; flex-direction: column; gap: 12px; }

    .auth-entry-btn {
      display: flex; align-items: center; gap: 14px;
      width: 100%; padding: 16px 18px;
      border-radius: 14px; cursor: pointer;
      font-family: inherit; text-align: start;
      transition: transform 120ms ease, filter 120ms ease, background 120ms ease;
      min-height: 64px;
    }
    .auth-entry-btn:hover { transform: translateY(-1px); filter: brightness(1.05); }
    .auth-entry-btn:active { transform: translateY(0); }

    .auth-entry-btn-primary {
      background: linear-gradient(135deg, #0A0A0A, #2A2520);
      color: #fff; border: none;
    }
    .auth-entry-btn-secondary {
      background: rgba(255,255,255,0.04);
      color: var(--text-primary, #F1F5F9);
      border: 1px solid rgba(255,255,255,0.10);
    }
    :root:not(.dark) .auth-entry-btn-secondary {
      background: rgba(15,23,42,0.04);
      border-color: rgba(15,23,42,0.10);
      color: #1E293B;
    }

    .auth-entry-icon {
      flex-shrink: 0;
      width: 40px; height: 40px;
      border-radius: 10px;
      display: flex; align-items: center; justify-content: center;
      background: rgba(255,255,255,0.12);
    }
    .auth-entry-btn-secondary .auth-entry-icon { background: rgba(10, 10, 10,0.12); color: #0A0A0A; }

    .auth-entry-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
    .auth-entry-title { font-size: 1rem; font-weight: 700; }
    .auth-entry-sub { font-size: 0.8rem; opacity: 0.85; }
    .auth-entry-btn-secondary .auth-entry-sub { color: var(--text-secondary, #94A3B8); }
  `;
  document.head.appendChild(style);
}
